import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';

import { environment } from '../../environments/environment';
import {
  pickNumber,
  pickString,
} from '../../core/utils/api-shape.util';
import {
  canAccessNavArea,
  canAccessPermission,
  type NavArea,
} from '../../core/utils/permission.util';

const AUTH_KEY = 'ims-auth';
const OTP_KEY = 'ims-otp-pending';
const RESET_KEY = 'ims-password-reset-pending';

export interface AuthResult {
  success: boolean;
  message?: string;
}

interface AuthSession {
  email: string;
  pin: string;
  userId: number;
  fullName: string;
  token: string;
  branchId?: number;
  branchIds?: number[];
  companyId?: number;
  companyName?: string;
  companyCode?: string;
  roles?: string[];
  permissions?: string[];
  authenticatedAt: string;
}

interface OtpSession {
  email: string;
  pin: string;
  userId: number;
  expiresAt: string;
  peekedOtp?: string;
}

interface ResetSession {
  email: string;
  peekedOtp?: string;
}

interface OtpSentResponse {
  userID?: number;
  userId?: number;
  message?: string;
  otp?: string;
  Otp?: string;
}

interface VerifyOtpResponse {
  success?: boolean;
  Success?: boolean;
  token?: string;
  Token?: string;
  message?: string;
  Message?: string;
  fullName?: string;
  FullName?: string;
  surname?: string;
  Surname?: string;
  emailAddress?: string;
  EmailAddress?: string;
  userID?: number;
  userId?: number;
  branchID?: number;
  branchId?: number;
  BranchID?: number;
  BranchId?: number;
  companyID?: number;
  companyId?: number;
  CompanyID?: number;
  companyName?: string;
  CompanyName?: string;
  companyCode?: string;
  CompanyCode?: string;
  branchIds?: number[];
  BranchIds?: number[];
  roles?: string[];
  Roles?: string[];
  permissions?: string[];
  Permissions?: string[];
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly authenticated = signal(false);
  private readonly userEmail = signal('');
  private readonly peekedOtp = signal('');

  readonly isAuthenticated = this.authenticated.asReadonly();
  readonly currentUserEmail = this.userEmail.asReadonly();

  /** Shown on OTP page when a dev peek succeeds (or mock OTP). */
  get demoOtp(): string {
    return (
      this.peekedOtp() ||
      this.getOtpSession()?.peekedOtp ||
      this.getResetSession()?.peekedOtp ||
      ''
    );
  }

  constructor() {
    this.restoreSession();
  }

  beginLogin(email: string, password: string): Observable<AuthResult> {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail || !password) {
      return of({
        success: false,
        message: 'Please enter both email and password.',
      });
    }

    if (!this.isValidEmail(normalizedEmail)) {
      return of({
        success: false,
        message: 'Please enter a valid email address.',
      });
    }

    sessionStorage.removeItem(AUTH_KEY);
    this.authenticated.set(false);
    this.userEmail.set('');
    this.peekedOtp.set('');

    return this.http
      .post<OtpSentResponse>(`${environment.apiUrl}/Auth/login`, {
        emailAddress: normalizedEmail,
        password,
      })
      .pipe(
        map((response) => {
          const userId = response.userID ?? response.userId;
          if (!userId) {
            return {
              success: false,
              message: 'Login succeeded but no user id was returned.',
            } satisfies AuthResult;
          }

          const code = (response.otp ?? response.Otp ?? '').trim();
          const otpSession: OtpSession = {
            email: normalizedEmail,
            pin: password,
            userId,
            expiresAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
            peekedOtp: code,
          };

          this.peekedOtp.set(code);
          sessionStorage.setItem(OTP_KEY, JSON.stringify(otpSession));
          return { success: true } satisfies AuthResult;
        }),
        catchError((error: unknown) =>
          of(this.toAuthError(error, 'Unable to sign in.')),
        ),
      );
  }

  verifyOtp(code: string): Observable<AuthResult> {
    const session = this.getOtpSession();

    if (!session) {
      return of({
        success: false,
        message: 'Your verification session expired. Please sign in again.',
      });
    }

    if (new Date(session.expiresAt).getTime() < Date.now()) {
      sessionStorage.removeItem(OTP_KEY);
      return of({
        success: false,
        message: 'Your verification code has expired. Please sign in again.',
      });
    }

    return this.http
      .post<VerifyOtpResponse>(`${environment.apiUrl}/Auth/verify-otp`, {
        userID: session.userId,
        otp: code.trim(),
      })
      .pipe(
        map((response) => {
          const success = response.success ?? response.Success ?? false;
          const token = response.token ?? response.Token ?? '';
          if (!success || !token) {
            return {
              success: false,
              message:
                response.message ??
                response.Message ??
                'Invalid verification code.',
            } satisfies AuthResult;
          }

          const fullName = formatDisplayName(
            pickString(response, 'fullName', 'FullName'),
            pickString(response, 'surname', 'Surname'),
          ) || session.email;

          const branchId = pickNumber(
            response,
            'branchID',
            'branchId',
            'BranchID',
            'BranchId',
          );
          const companyId = pickNumber(
            response,
            'companyID',
            'companyId',
            'CompanyID',
            'CompanyId',
          );
          const companyName =
            pickString(response, 'companyName', 'CompanyName').trim() ||
            pickString(response, 'companyCode', 'CompanyCode').trim();

          const roles = toStringList(response.roles ?? response.Roles);
          const permissions = toStringList(
            response.permissions ?? response.Permissions,
          );
          const branchIds = toNumberList(
            response.branchIds ?? response.BranchIds,
          );
          if (branchId > 0 && !branchIds.includes(branchId)) {
            branchIds.unshift(branchId);
          }

          const authSession: AuthSession = {
            email: session.email,
            pin: session.pin,
            userId: session.userId,
            fullName,
            token,
            branchId: branchId > 0 ? branchId : undefined,
            branchIds,
            companyId: companyId > 0 ? companyId : undefined,
            companyName,
            companyCode: pickString(response, 'companyCode', 'CompanyCode').trim(),
            roles,
            permissions,
            authenticatedAt: new Date().toISOString(),
          };

          sessionStorage.setItem(AUTH_KEY, JSON.stringify(authSession));
          sessionStorage.removeItem(OTP_KEY);
          this.authenticated.set(true);
          this.userEmail.set(session.email);
          this.peekedOtp.set('');
          return { success: true } satisfies AuthResult;
        }),
        catchError((error: unknown) =>
          of(
            this.toAuthError(
              error,
              'Invalid verification code. Please try again.',
            ),
          ),
        ),
      );
  }

  requestPasswordReset(email: string): Observable<AuthResult> {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      return of({
        success: false,
        message: 'Please enter your email address.',
      });
    }

    if (!this.isValidEmail(normalizedEmail)) {
      return of({
        success: false,
        message: 'Please enter a valid email address.',
      });
    }

    return this.http
      .post<OtpSentResponse>(`${environment.apiUrl}/Auth/forgot-password`, {
        emailAddress: normalizedEmail,
      })
      .pipe(
        map((response) => {
          const code = (response.otp ?? response.Otp ?? '').trim();
          sessionStorage.setItem(
            RESET_KEY,
            JSON.stringify({
              email: normalizedEmail,
              peekedOtp: code,
            } satisfies ResetSession),
          );
          this.peekedOtp.set(code);
          return {
            success: true,
            message: code ? `Reset OTP: ${code}` : undefined,
          } satisfies AuthResult;
        }),
        catchError((error: unknown) =>
          of(this.toAuthError(error, 'Unable to start password reset.')),
        ),
      );
  }

  resetPassword(
    email: string,
    newPassword: string,
    resetOtp: string,
  ): Observable<AuthResult> {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail || !newPassword || !resetOtp) {
      return of({
        success: false,
        message: 'Email, new password, and reset OTP are required.',
      });
    }

    return this.http
      .post(
        `${environment.apiUrl}/Auth/reset-password`,
        {
          emailAddress: normalizedEmail,
          newPassword,
          resetOtp: resetOtp.trim(),
        },
        { responseType: 'text' },
      )
      .pipe(
        map(() => {
          this.clearPendingReset();
          this.peekedOtp.set('');
          return { success: true } satisfies AuthResult;
        }),
        catchError((error: unknown) =>
          of(this.toAuthError(error, 'Unable to reset password.')),
        ),
      );
  }

  canResetPassword(email: string): boolean {
    const session = this.getResetSession();
    if (!session) {
      return false;
    }

    return session.email === email.trim().toLowerCase();
  }

  clearPendingReset(): void {
    sessionStorage.removeItem(RESET_KEY);
  }

  hasPendingOtp(): boolean {
    const session = this.getOtpSession();
    if (!session) {
      return false;
    }

    if (new Date(session.expiresAt).getTime() < Date.now()) {
      sessionStorage.removeItem(OTP_KEY);
      return false;
    }

    return true;
  }

  getPendingEmail(): string {
    return this.getOtpSession()?.email ?? '';
  }

  getMaskedEmail(): string {
    const email = this.getPendingEmail();
    if (!email) {
      return '';
    }

    const [local, domain] = email.split('@');
    if (!local || !domain) {
      return email;
    }

    if (local.length <= 2) {
      return `${local[0] ?? ''}***@${domain}`;
    }

    return `${local[0]}${'*'.repeat(Math.max(local.length - 2, 3))}${local.at(-1)}@${domain}`;
  }

  resendOtp(): Observable<AuthResult> {
    const session = this.getOtpSession();
    if (!session) {
      return of({
        success: false,
        message: 'Unable to resend code. Please sign in again.',
      });
    }

    return this.beginLogin(session.email, session.pin);
  }

  /** Clears local session only (used when the server already invalidated the JWT). */
  clearLocalSession(): void {
    sessionStorage.removeItem(AUTH_KEY);
    sessionStorage.removeItem(OTP_KEY);
    sessionStorage.removeItem(RESET_KEY);
    this.authenticated.set(false);
    this.userEmail.set('');
    this.peekedOtp.set('');
  }

  logout(): void {
    const token = this.getAuthSession()?.token;
    this.clearLocalSession();

    if (token) {
      this.http
        .post(
          `${environment.apiUrl}/Auth/logout`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        )
        .subscribe({ error: () => undefined });
    }
  }

  clearPendingOtp(): void {
    sessionStorage.removeItem(OTP_KEY);
    this.peekedOtp.set('');
  }

  getCredentials(): { email: string; pin: string } | null {
    const session = this.getAuthSession();
    if (!session?.email || !session.pin) {
      return null;
    }

    return { email: session.email, pin: session.pin };
  }

  /** Logged-in user's home branch (from UserCredentials.BranchID). */
  currentBranchId(): number {
    return this.getAuthSession()?.branchId ?? 0;
  }

  /** Home branch plus IMS.UserBranches assignments. */
  allowedBranchIds(): number[] {
    const session = this.getAuthSession();
    const ids = [...(session?.branchIds ?? [])];
    const home = session?.branchId ?? 0;
    if (home > 0 && !ids.includes(home)) {
      ids.unshift(home);
    }
    return ids.filter((id) => id > 0);
  }

  currentCompanyId(): number {
    return this.getAuthSession()?.companyId ?? 0;
  }

  currentCompanyName(): string {
    const session = this.getAuthSession();
    return session?.companyName?.trim() || session?.companyCode?.trim() || '';
  }

  currentRoles(): string[] {
    return [...(this.getAuthSession()?.roles ?? [])];
  }

  currentRoleLabel(): string {
    return this.currentRoles()[0] || 'User';
  }

  currentPermissions(): string[] {
    return [...(this.getAuthSession()?.permissions ?? [])];
  }

  hasPermission(code: string): boolean {
    const session = this.getAuthSession();
    return canAccessPermission(code, session?.permissions, session?.roles);
  }

  canAccessNav(area: NavArea): boolean {
    const session = this.getAuthSession();
    return canAccessNavArea(area, session?.roles, session?.permissions);
  }

  /** Persist branch after settings/branches API load. */
  setUserBranchId(branchId: number): void {
    if (!Number.isFinite(branchId) || branchId <= 0) {
      return;
    }

    const session = this.getAuthSession();
    if (!session) {
      return;
    }

    if (session.branchId === branchId) {
      return;
    }

    session.branchId = branchId;
    sessionStorage.setItem(AUTH_KEY, JSON.stringify(session));
  }

  /** Logged-in user's database id (for CreatedByUserID / ReportedByUserID). */
  currentUserId(): number {
    return this.getAuthSession()?.userId ?? 0;
  }

  /** Display name from profile (FullName + Surname), falling back to email. */
  currentUserName(): string {
    const session = this.getAuthSession();
    const stored = session?.fullName?.trim() || '';
    return dedupeRepeatedSurname(stored) || session?.email || '';
  }

  private restoreSession(): void {
    const session = this.getAuthSession();
    if (!session?.email || !session.token) {
      return;
    }

    this.authenticated.set(true);
    this.userEmail.set(session.email);
  }

  private getAuthSession(): AuthSession | null {
    const raw = sessionStorage.getItem(AUTH_KEY);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as AuthSession;
    } catch {
      sessionStorage.removeItem(AUTH_KEY);
      return null;
    }
  }

  private getOtpSession(): OtpSession | null {
    const raw = sessionStorage.getItem(OTP_KEY);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as OtpSession;
    } catch {
      sessionStorage.removeItem(OTP_KEY);
      return null;
    }
  }

  private getResetSession(): ResetSession | null {
    const raw = sessionStorage.getItem(RESET_KEY);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as ResetSession;
    } catch {
      sessionStorage.removeItem(RESET_KEY);
      return null;
    }
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private toAuthError(error: unknown, fallback: string): AuthResult {
    if (error instanceof HttpErrorResponse) {
      if (typeof error.error === 'string') {
        const text = error.error.trim();
        if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
          return {
            success: false,
            message:
              'API returned a web page instead of JSON. Confirm the gateway is on https://localhost:7077 and restart both API and ng serve.',
          };
        }
        if (text) {
          return { success: false, message: text };
        }
      }

      if (error.error?.message) {
        return { success: false, message: String(error.error.message) };
      }

      if (error.error?.Message) {
        return { success: false, message: String(error.error.Message) };
      }

      if (error.error?.title) {
        return { success: false, message: String(error.error.title) };
      }

      if (error.status === 403) {
        return {
          success: false,
          message:
            'This user must be assigned to an active company before signing in.',
        };
      }

      if (error.status === 401) {
        return { success: false, message: 'Invalid email or password.' };
      }

      if (error.status === 0) {
        return {
          success: false,
          message:
            'Cannot reach the API. Open https://localhost:7077/swagger once to trust the certificate, then retry.',
        };
      }
    }

    return { success: false, message: fallback };
  }
}

function toStringList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => String(item ?? '').trim())
    .filter((item) => item.length > 0);
}

function toNumberList(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item) && item > 0);
}

function formatDisplayName(fullName?: string | null, surname?: string | null): string {
  const given = String(fullName ?? '').trim();
  const last = String(surname ?? '').trim();
  if (!given) {
    return last;
  }
  if (!last) {
    return given;
  }

  const givenLower = given.toLowerCase();
  const lastLower = last.toLowerCase();
  if (givenLower.endsWith(lastLower) || givenLower.includes(` ${lastLower}`)) {
    return given;
  }

  return `${given} ${last}`.trim();
}

function dedupeRepeatedSurname(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  while (
    parts.length >= 2 &&
    parts[parts.length - 1].toLowerCase() === parts[parts.length - 2].toLowerCase()
  ) {
    parts.pop();
  }
  return parts.join(' ');
}
